/**
 * Centralized language configuration
 * All forms should import from here to ensure consistency
 */

// Standard language list with codes and names
// This should match the backend languages collection
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'zh', name: 'Chinese (Mandarin)' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'mr', name: 'Marathi' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ur', name: 'Urdu' },
  { code: 'fa', name: 'Farsi/Persian' },
  { code: 'tr', name: 'Turkish' },
  { code: 'pl', name: 'Polish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ro', name: 'Romanian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'th', name: 'Thai' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ms', name: 'Malay' },
  { code: 'tl', name: 'Tagalog/Filipino' },
  { code: 'sw', name: 'Swahili' },
  { code: 'am', name: 'Amharic' },
  { code: 'so', name: 'Somali' },
  { code: 'my', name: 'Burmese' },
  { code: 'km', name: 'Khmer/Cambodian' },
  { code: 'hmn', name: 'Hmong' },
  { code: 'it', name: 'Italian' },
];

// Create a lookup map for quick access
const languageMap = LANGUAGES.reduce((acc, lang) => {
  acc[lang.code] = lang.name;
  acc[lang.code.toLowerCase()] = lang.name;
  acc[lang.name.toLowerCase()] = lang.name;
  return acc;
}, {});

/**
 * Get language name from code or name
 * Handles both codes (es) and full names (Spanish)
 */
export const getLanguageName = (codeOrName) => {
  if (!codeOrName) return 'Unknown';
  
  // First try exact match by code
  const byCode = languageMap[codeOrName];
  if (byCode) return byCode;
  
  // Try lowercase match
  const byLower = languageMap[codeOrName.toLowerCase()];
  if (byLower) return byLower;
  
  // Try to find by partial name match
  const found = LANGUAGES.find(l => 
    l.name.toLowerCase().includes(codeOrName.toLowerCase()) ||
    codeOrName.toLowerCase().includes(l.name.toLowerCase())
  );
  if (found) return found.name;
  
  // Return original if no match
  return codeOrName;
};

/**
 * Get language code from name
 */
export const getLanguageCode = (name) => {
  if (!name) return null;
  
  // First check if it's already a code
  const lang = LANGUAGES.find(l => l.code === name || l.code === name.toLowerCase());
  if (lang) return lang.code;
  
  // Find by name
  const byName = LANGUAGES.find(l => 
    l.name.toLowerCase() === name.toLowerCase() ||
    l.name.toLowerCase().includes(name.toLowerCase())
  );
  return byName ? byName.code : name;
};

/**
 * Normalize a language value to standard format
 * Use this when storing languages to ensure consistency
 */
export const normalizeLanguage = (value) => {
  if (!value) return null;
  
  // Find the language
  const lang = LANGUAGES.find(l => 
    l.code === value || 
    l.code === value.toLowerCase() ||
    l.name.toLowerCase() === value.toLowerCase()
  );
  
  // Return the standard name (for display) or code (for storage)
  return lang ? lang.name : value;
};

/**
 * Check if two language values refer to the same language
 */
export const isSameLanguage = (lang1, lang2) => {
  if (!lang1 || !lang2) return false;
  
  const code1 = getLanguageCode(lang1);
  const code2 = getLanguageCode(lang2);
  
  return code1 && code2 && code1.toLowerCase() === code2.toLowerCase();
};

export default LANGUAGES;
